<template>
    <div class="mb-4">
        <div class="bg-gray-100 rounded p-4">
            <!-- Header với nút chuyển mặt -->
            <div class="d-flex align-items-center justify-content-between mb-3">
                <h2 class="h5 fw-semibold d-flex align-items-center mb-0">
                    <i class="fas fa-upload mr-2"></i>
                    <span>{{ currentSide === "front" ? "Tải lên mặt trước" : "Tải lên mặt sau" }}</span>
                </h2>
                <button
                    @click="switchSide"
                    class="btn btn-outline-primary btn-sm d-flex align-items-center"
                    type="button"
                >
                    <i class="fas fa-exchange-alt mr-2"></i> Chuyển mặt
                </button>
            </div>

            <!-- Alert lỗi OCR nếu có -->
            <div
                v-if="ocrError"
                class="alert alert-danger py-2 px-3 small mb-3 d-flex align-items-center justify-content-between"
            >
                <span>
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    {{ ocrError }}
                </span>
                <button
                    type="button"
                    class="btn btn-sm btn-link text-danger p-0 ml-2"
                    @click="retryOCR"
                >
                    Thử lại
                </button>
            </div>

            <!-- Thanh tiến trình tải ảnh -->
            <div class="mb-3">
                <div class="d-flex align-items-center justify-content-between small text-muted mb-2">
                    <span :class="{ 'text-success fw-medium': frontImage }" class="d-flex align-items-center">
                        <i v-if="frontImage" class="fas fa-check mr-1"></i> Mặt trước
                    </span>
                    <span :class="{ 'text-success fw-medium': backImage }" class="d-flex align-items-center">
                        <i v-if="backImage" class="fas fa-check mr-1"></i> Mặt sau
                    </span>
                </div>
                <div class="progress" style="height: 6px">
                    <div
                        class="progress-bar bg-primary transition-all"
                        role="progressbar"
                        :style="{ width: progressWidth + '%' }"
                        :aria-valuenow="progressWidth"
                        aria-valuemin="0"
                        aria-valuemax="100"
                    ></div>
                </div>
            </div>

            <!-- Khung xem trước & Tải ảnh -->
            <div
                class="image-preview d-flex align-items-center justify-content-center mb-3 position-relative upload-zone border rounded bg-white"
                :class="{ dragover: isDragOver }"
                style="height: 220px; cursor: pointer; border: 2px dashed #cbd5e1 !important;"
                @click="triggerFileUpload"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="handleDrop"
            >
                <img
                    v-if="getCurrentImage()"
                    :src="getCurrentImage()"
                    :alt="currentSide === 'front' ? 'Mặt trước căn cước' : 'Mặt sau căn cước'"
                    class="img-fluid h-100 w-100"
                    style="object-fit: contain"
                />

                <div v-else class="text-center image-placeholder p-3">
                    <i class="fas fa-cloud-upload-alt fa-3x mb-2 text-muted"></i>
                    <p class="fw-medium mb-1">
                        {{ currentSide === "front" ? "Tải lên mặt trước căn cước" : "Tải lên mặt sau căn cước" }}
                    </p>
                    <p class="small mb-2 text-primary">Nhấp để chọn file hoặc kéo thả ảnh vào đây</p>
                    <p class="small text-muted mb-0">
                        {{ currentSide === "front" ? "Mặt có ảnh, họ tên, số CCCD, ngày sinh" : "Mặt có ngày cấp, có giá trị đến, cơ quan cấp" }}
                    </p>
                </div>

                <!-- Loading overlay OCR -->
                <transition name="fade">
                    <div
                        v-if="isScanning"
                        class="position-absolute top-0 left-0 w-100 h-100 loading-overlay d-flex align-items-center justify-content-center bg-white-50"
                        style="background: rgba(255, 255, 255, 0.9);"
                    >
                        <div class="d-flex flex-column align-items-center">
                            <div class="spinner-border text-primary mb-2" role="status"></div>
                            <span class="text-primary fw-medium mb-2">
                                Đang xử lý {{ scanningStep === "front" ? "mặt trước" : "mặt sau" }}...
                            </span>
                            <div class="progress w-75 mb-2" style="height: 6px">
                                <div class="progress-bar bg-primary" :style="{ width: ocrProgress + '%' }"></div>
                            </div>
                            <small class="text-muted">{{ ocrProgress }}%</small>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Checkbox đồng ý cam kết -->
            <div class="mb-3 custom-control custom-checkbox">
                <input
                    type="checkbox"
                    class="custom-control-input"
                    id="agreeCheck"
                    :checked="isAgreed"
                    @change="$emit('update:isAgreed', $event.target.checked)"
                />
                <label class="custom-control-label small text-muted" for="agreeCheck">
                    Tôi cam kết các thông tin trên là chính xác và hoàn toàn chịu trách nhiệm trước pháp luật.
                </label>
            </div>

            <!-- Hàng nút điều khiển -->
            <div class="row no-gutters mb-3">
                <div class="col pr-1">
                    <button
                        @click="triggerFileUpload"
                        class="btn btn-primary btn-block d-flex align-items-center justify-content-center"
                        type="button"
                    >
                        <i class="el-icon-upload2 mr-1"></i> Chọn file
                    </button>
                </div>

                <div class="col px-1">
                    <button
                        @click="startOCR"
                        :disabled="!frontImage || !backImage || !isAgreed || isScanning"
                        class="btn btn-success btn-block d-flex align-items-center justify-content-center"
                        type="button"
                    >
                        <i class="el-icon-magic-stick mr-1"></i> Quét & Xác thực
                    </button>
                </div>

                <div v-if="getCurrentImage()" class="col-auto px-1">
                    <button
                        @click="removeCurrentImage"
                        :disabled="isScanning"
                        class="btn btn-outline-danger d-flex align-items-center justify-content-center"
                        type="button"
                        title="Xóa ảnh hiện tại"
                    >
                        <i class="el-icon-delete"></i>
                    </button>
                </div>

                <div class="col-auto pl-1">
                    <button
                        @click="resetAll"
                        :disabled="isScanning"
                        class="btn btn-secondary d-flex align-items-center justify-content-center"
                        type="button"
                        title="Làm mới"
                    >
                        <i class="el-icon-refresh-left"></i>
                    </button>
                </div>
            </div>

            <input
                ref="fileInput"
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                @change="handleFileUpload"
                class="d-none"
            />
        </div>

        <!-- Khung Hướng dẫn -->
        <div class="alert alert-info border-info mt-3 mb-0" style="background-color: rgb(239, 246, 255);">
            <div class="d-flex align-items-start">
                <i class="fas fa-info-circle mr-2 mt-1 text-info"></i>
                <div class="text-info small">
                    <p class="fw-bold mb-1" style="font-size: 14px">Hướng dẫn tải ảnh:</p>
                    <ul class="mb-0 pl-3">
                        <li><strong>Mặt trước:</strong> Chứa ảnh, họ tên, số CCCD, ngày sinh, địa chỉ</li>
                        <li><strong>Mặt sau:</strong> Chứa ngày cấp, có giá trị đến, cơ quan cấp</li>
                        <li><strong>Định dạng:</strong> JPG, PNG, JPEG (tối đa 10MB)</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    PRESIGN_UPLOAD,
    CHECK_IDENTITY_CARD,
} from "@/core/services/store/profile.module";
import { CCCDValidator } from "./cccdValidator.js";

export default {
    name: "OcrImageUploader",
    props: {
        isAgreed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentSide: "front",
            frontImage: null,
            backImage: null,
            frontFile: null,
            backFile: null,
            frontIdNumber: null,
            isDragOver: false,
            isScanning: false,
            scanningStep: "front",
            ocrProgress: 0,
            ocrError: null,
        };
    },
    computed: {
        progressWidth() {
            let count = 0;
            if (this.frontImage) count += 50;
            if (this.backImage) count += 50;
            return count;
        },
    },
    methods: {
        getCurrentImage() {
            return this.currentSide === "front" ? this.frontImage : this.backImage;
        },
        switchSide() {
            this.currentSide = this.currentSide === "front" ? "back" : "front";
        },
        triggerFileUpload() {
            this.$refs.fileInput.click();
        },
        handleFileUpload(event) {
            const file =
                (event.target.files && event.target.files[0]) ||
                (event.dataTransfer && event.dataTransfer.files[0]);
            if (!file) return;

            if (file.type !== "image/jpeg" && file.type !== "image/png" && file.type !== "image/jpg") {
                this.ocrError = "Định dạng không hợp lệ. Vui lòng chọn ảnh JPG hoặc PNG.";
                if (event.target && event.target.value) event.target.value = "";
                return;
            }

            this.ocrError = null;
            if (this.currentSide === "front") {
                this.frontFile = file;
            } else {
                this.backFile = file;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                if (this.currentSide === "front") {
                    this.frontImage = e.target.result;
                } else {
                    this.backImage = e.target.result;
                }
            };
            reader.readAsDataURL(file);

            if (event.target && event.target.value) event.target.value = "";
        },
        handleDrop(event) {
            this.isDragOver = false;
            this.handleFileUpload(event);
        },
        removeCurrentImage() {
            if (this.currentSide === "front") {
                this.frontImage = null;
                this.frontFile = null;
            } else {
                this.backImage = null;
                this.backFile = null;
            }
        },
        startOCR() {
            const currentFile = this.currentSide === "front" ? this.frontFile : this.backFile;
            if (!currentFile) {
                this.ocrError = "Vui lòng chọn hoặc tải ảnh lên trước khi bấm quét.";
                return;
            }
            this.processUploadAndCheckCard(currentFile);
        },
        retryOCR() {
            this.ocrError = null;
            this.startOCR();
        },
        async processUploadAndCheckCard(file) {
            this.isScanning = true;
            this.scanningStep = this.currentSide;
            this.ocrProgress = 15;
            this.ocrError = null;

            try {
                // 1. Presign Upload
                const presignRes = await this.$store.dispatch(PRESIGN_UPLOAD, {
                    file_name: file.name,
                    content_type: file.type || "image/jpeg",
                });
                const uploadData = presignRes.data || presignRes;
                const { presigned_url, public_url, headers = {} } = uploadData;
                this.ocrProgress = 45;

                // 2. PUT S3
                const uploadResponse = await fetch(presigned_url, {
                    method: "PUT",
                    headers: {
                        "Content-Type": headers["Content-Type"] || file.type || "image/jpeg",
                        "x-amz-acl": headers["x-amz-acl"] || "public-read",
                    },
                    body: file,
                });
                if (!uploadResponse.ok) throw new Error("Tải ảnh lên máy chủ lưu trữ thất bại.");
                this.ocrProgress = 75;

                // 3. OCR Check
                const checkRes = await this.$store.dispatch(CHECK_IDENTITY_CARD, {
                    side: this.currentSide,
                    url: public_url,
                });
                this.ocrProgress = 100;

                // 4. Validate & Emit
                const cardData = checkRes.data || checkRes;
                this.validateAndEmitResult(cardData);
            } catch (error) {
                console.error("❌ Lỗi OCR:", error);
                const friendlyMessage = error?.data?.message || error?.message || "Không thể xử lý ảnh căn cước. Vui lòng thử lại.";
                this.ocrError = friendlyMessage;
                if (this.$message) {
                    this.$message.error({ message: friendlyMessage, duration: 5000, showClose: true });
                }
            } finally {
                this.isScanning = false;
            }
        },
        validateAndEmitResult(data) {
            if (!data) return;
            const payload = data.data || data;
            const qrPayload = data.qr?.data || {};

            // Validator kiểm tra nhầm mặt
            const sideError = CCCDValidator.checkWrongSide(this.currentSide, payload, qrPayload);
            if (sideError) return this.handleError(sideError);

            const scannedFullName = payload.full_name || qrPayload.full_name || "";
            const scannedIdNumber = payload.id_number || qrPayload.id_number || "";
            const scannedDob = payload.date_of_birth || qrPayload.date_of_birth || "";

            const frontError = CCCDValidator.checkFrontInvalid(this.currentSide, scannedFullName, scannedIdNumber, scannedDob);
            if (frontError) return this.handleError(frontError);

            const scannedExpiry = payload.expiry_date || payload.date_of_expiry || "";
            const scannedIssueDate = payload.date_of_issue || payload.issued_date || "";

            const backError = CCCDValidator.checkBackInvalid(this.currentSide, scannedExpiry, scannedIssueDate);
            if (backError) return this.handleError(backError);

            const mismatchError = CCCDValidator.checkIdMismatch(this.currentSide, scannedIdNumber, this.frontIdNumber);
            if (mismatchError) return this.handleError(mismatchError);

            if (this.currentSide === "front" && scannedIdNumber) {
                this.frontIdNumber = scannedIdNumber;
            }

            // Gửi dữ liệu đã bóc tách sang component cha
            this.$emit("scanned", {
                data: {
                    full_name: scannedFullName,
                    id_number: scannedIdNumber,
                    date_of_birth: scannedDob,
                    gender: payload.gender || qrPayload.gender,
                    nationality: payload.nationality || "Việt Nam",
                    place_of_birth: payload.place_of_birth || payload.place_of_origin || qrPayload.place_of_origin || "",
                    place_of_residence: payload.place_of_residence || qrPayload.place_of_residence,
                    expiry_date: scannedExpiry,
                    issued_date: scannedIssueDate,
                    issued_place: payload.place_of_issue,
                },
            });

            if (this.$message) {
                this.$message({
                    type: "success",
                    message: "Quét thành công. Vui lòng kiểm tra lại các trường được đánh dấu “Đã quét”.",
                    duration: 5000,
                    showClose: true,
                });
            }
        },
        handleError(message) {
            this.ocrError = message;
            this.$emit("scan-error", message);
            if (this.$message) {
                this.$message.error({ message, duration: 6000, showClose: true });
            }
        },
        resetAll() {
            this.frontImage = null;
            this.backImage = null;
            this.frontFile = null;
            this.backFile = null;
            this.frontIdNumber = null;
            this.ocrError = null;
            this.ocrProgress = 0;
            this.isScanning = false;
            if (this.$refs.fileInput) this.$refs.fileInput.value = "";
            this.$emit("reset");
        },
    },
};
</script>

